// 261005 E1-P1 — khuôn chung của MỌI chặng: tiêu đề + nút chính + menu ⋯ + "Cần bạn xem" + thân.
// Mỗi XxxStage tự render <StageFrame> (vỏ chỉ chọn component chặng).
import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Modal, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { WorkbenchStageKey } from '../../../types/adaptationWorkbench';
import { humanizeCodes } from '../adaptationUtils';
import { useWorkbenchCtx } from './WorkbenchContext';
import WbSlot, { Bang, Strip } from './WbSlot';
import { DangerCopyView, lockReason, reasonTarget, STAGE_LABEL, withoutScanNudge } from './workbenchView';
import { nsNeedReasonView, nsStepLabel } from './newStoryRunView';
import ChainLoop from './ChainLoop';
import { boxRowsOf } from './chainProgressView';
import { notRunBoxOf, SOURCE_RUNNING_NOTE, STAGE_WHEN, stageDesc } from './stageCopy';
import { formatJobStepProgress } from './jobStepProgress';
import { friendlyReasonText, looksTechnical, technicalDetailText } from './jobErrorText';
import { scriptTableDuringChain } from './episodeRowView';
import { withGroupDividers } from './topMenuView';

const { Text } = Typography;

export interface StageRedo {
  label: string;
  /** Có ⇒ hỏi lại (Modal.confirm) trước khi chạy `run`. */
  confirm?: DangerCopyView;
  run: () => void;
  disabled?: boolean;
}

/** Cổng AI của chặng (nếu có) — chỉ để "Chi tiết kỹ thuật" hiện model / trạng thái. */
export interface StageGateInfo { label: string; gate?: { status?: string; model?: string } | null }

interface Props {
  stage: WorkbenchStageKey;
  primary?: React.ReactNode;
  redo?: StageRedo;
  gate?: StageGateInfo;
  /** Phần dữ liệu kỹ thuật riêng của chặng (vd Scene Plan & bằng chứng ở ⑤). */
  techExtra?: React.ReactNode;
  /**
   * `body` (mặc định): khoá cả thân (inert + pointer-events:none). `buttons` (261005 E1-P2, chặng ⑤): vẫn cho đọc / chọn / cuộn —
   * khung chỉ hiện dòng lý do khoá, các nút ghi của chặng tự khoá theo `stageState`.
   */
  lockMode?: 'body' | 'buttons';
  /** 261005 E5 — ⑤ tự hiện "Cần xem" dưới tiêu đề tập (gộp cấp chặng + cấp tập) nên khuôn không vẽ danh sách riêng. */
  hideNeeds?: boolean;
  children: React.ReactNode;
}

const StageFrame: React.FC<Props> = ({ stage, primary, redo, gate, techExtra, lockMode = 'body', hideNeeds, children }) => {
  const { workbench, goTo, slots, data, topMenuExtra, bgJob, navRows, startTour } = useWorkbenchCtx();
  const nameOf = (id: string) => {
    const o = data.record?.outline as { characters?: Array<{ id: string; name: string; localizedName?: string }>; locations?: Array<{ id: string; name: string; localizedName?: string }> } | null | undefined;
    const e = [...(o?.characters ?? []), ...(o?.locations ?? [])].find((x) => x.id === id);
    return (e?.localizedName?.trim() || e?.name) ?? id;
  };
  const [techOpen, setTechOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const st = workbench?.stages.find((s) => s.key === stage);
  const locked = st?.state === 'locked';
  // 3h2 (V12 / K4 / K5): chuỗi «Truyện mới» chưa chạy tới / đang chạy bước này ⇒ hộp giải thích thay thân bước (①: giữ thân, chỉ thêm câu + bỏ khối «Cần bạn xem»).
  const navRow = navRows?.find((r) => r.key === stage);
  const chainHold = !!navRow?.byChain && (navRow.status === 'notRun' || navRow.status === 'running');
  // 3k-a: ⑤ có tập đã viết ⇒ cho thấy BẢNG TẬP (chip Đạt · Đang sửa · Chờ…) thay vì hộp «máy đang chạy» — như mockup Rev 3 trạng thái «máy đang tự sửa».
  const replaceBody = chainHold && stage !== 'source' && !(stage === 'script' && scriptTableDuringChain(workbench?.episodes));
  const lockBody = locked && lockMode === 'body';
  // F11: đang nạp/quét (phiên này hoặc nơi khác) ⇒ không nhắc "chưa quét" — nhắc khi chưa nạp xong là sai thời điểm.
  const scanning = ['crawl', 'scan'].includes(bgJob.bgJob?.key ?? '') || (workbench?.jobs.running ?? []).some((j) => j.kind === 'crawl' || j.kind === 'scan');
  const reasonsAll = st ? withoutScanNudge([...st.blockers.map((r) => ({ r, blocker: true })), ...st.attention.map((r) => ({ r, blocker: false }))].map((x) => ({ ...x, code: x.r.code })), scanning) : [];
  const reasons = chainHold ? [] : reasonsAll;   // chuỗi đang giữ bước này: «Đã có 3 chương nhưng chưa quét» không phải việc của người (smoke 26/10)

  // Khoá: chặn cả phím (inert) lẫn chuột (CSS pointer-events). Dữ liệu vẫn hiện.
  useEffect(() => {
    const el = bodyRef.current as (HTMLDivElement & { inert?: boolean }) | null;
    if (el) el.inert = lockBody;
  }, [lockBody]);

  // 3k-a: MỘT «⋯» cho mỗi bước — mục của bước · danh sách tập · tập đang mở (đã GỘP, có đường kẻ giữa các nhóm: `topMenuView`) · Làm lại bước · Chi tiết kỹ thuật của bước · Hướng dẫn.
  // 3k-c: bỏ mục «Sổ duyệt đầy đủ» — hàng «Sổ duyệt» ở cột bước bên trái là cửa chính (còn nút «Mở sổ duyệt» ở dải).
  const menuItems: NonNullable<MenuProps['items']> = [
    ...withGroupDividers(topMenuExtra).map((x, i) => (x === null ? { type: 'divider' as const, key: `d${i}` } : { key: `x:${x.key}`, label: x.reason ? <span title={x.reason}>{x.label}</span> : x.label, danger: x.danger, disabled: x.disabled })),
    ...(topMenuExtra.length ? [{ type: 'divider' as const, key: 'dx' }] : []),
    ...(redo ? [{ key: 'redo', label: 'Làm lại bước này', disabled: redo.disabled || locked }] : []),
    { key: 'tech', label: 'Chi tiết kỹ thuật của bước' },
    { type: 'divider' as const, key: 'dt' },
    { key: 'tour', label: 'Hướng dẫn' },
  ];
  const onMenu = ({ key }: { key: string }) => {
    if (key === 'tech') { setTechOpen(true); return; }
    if (key === 'tour') { startTour(); return; }
    if (key.startsWith('x:')) { topMenuExtra.find((x) => `x:${x.key}` === key)?.onSelect(); return; }
    if (key === 'redo' && redo) {
      if (redo.confirm) {
        Modal.confirm({
          title: redo.confirm.title, content: redo.confirm.content, okText: redo.confirm.okText, cancelText: 'Thôi',
          okButtonProps: { danger: redo.confirm.danger }, onOk: () => redo.run(),
        });
      } else redo.run();
    }
  };

  const stageJobs = workbench
    ? [
      ...workbench.jobs.running.filter((j) => j.stage === stage).map((j) => ({ ...j, state: 'running' as const })),
      ...workbench.jobs.failed.filter((j) => j.stage === stage).map((j) => ({ ...j, state: 'failed' as const })),
    ]
    : [];

  return (
    <section aria-label={STAGE_LABEL[stage]}>
      {stage !== 'script' && <h1 className="wb-h1">{STAGE_LABEL[stage]}</h1>}
      {/* 3h1 (V11): mỗi bước một dòng — làm gì, bạn cần làm gì. */}
      {/* 3k-a: trang kịch bản (⑤) bỏ dòng mô tả bước (mockup Rev 3); «Hướng dẫn» trong «⋯» thay chỗ. */}
      {stage !== 'script' && <p className="wb-desc" data-tour="desc">{stageDesc(stage)}</p>}
      {/* 261005 E5: nút chính + ⋯ nằm ở thanh trên của vỏ (portal), bước giữ nguyên props / handler. */}
      <WbSlot to={slots.topPrimary}>
        {replaceBody ? null : primary}
        <Dropdown menu={{ items: menuItems, onClick: onMenu }} trigger={['click']}>
          <Button type="text" aria-label="Thêm tác vụ của bước" data-tour="more">⋯</Button>
        </Dropdown>
      </WbSlot>

      {locked && workbench && !chainHold && (
        <div className="wb-strips"><Strip tone="idle">{`Bước này đang khoá: ${lockReason(stage, workbench.stages)} Dữ liệu vẫn xem được, nút ghi đã khoá.`}</Strip></div>
      )}

      {reasons.length > 0 && !hideNeeds && (
        <div className="wb-needs" role="region" aria-label="Cần bạn xem">
          <div className="wb-needs-h">Cần bạn xem · {reasons.length}</div>
          {reasons.map(({ r, blocker }, i) => {
            // 261023 E3-P3c2b (H13): chuỗi truyện mới ở STOP 1 — «cần chốt tổng quan» không đòi chốt riêng mà trỏ lên dải (không «Đi tới»).
            const up = nsNeedReasonView(workbench?.appendRun, r);
            return (
              <div className="wb-need" key={`${r.code}-${r.episode ?? ''}-${i}`}>
                <span className="wb-need-t"><Bang tone={blocker ? 'bad' : 'warn'} />{up ? up.text : humanizeCodes(friendlyReasonText(r.message), nameOf)}</span>
                {!up && <Button size="small" onClick={() => goTo(reasonTarget(stage, r, workbench?.episodes.map((e) => ({ ep: e.ep, written: e.written }))))}>Đi tới</Button>}
              </div>
            );
          })}
        </div>
      )}

      {chainHold && stage === 'source' && (
        <div className="wb-notrun-note" data-tour="notrun" role="note">{navRow?.status === 'running' ? SOURCE_RUNNING_NOTE : STAGE_WHEN.source}</div>
      )}
      {chainHold && stage === 'source' && navRow?.status === 'running' && <ChainLoop rows={boxRowsOf(workbench?.chainProgress, 'source')} variant="box" />}
      {replaceBody && workbench ? (() => {
        const box = notRunBoxOf(stage, navRow!.status === 'running' ? 'running' : 'notRun', locked ? lockReason(stage, workbench.stages) : null, nsStepLabel(workbench.appendRun?.step ?? workbench.newStoryRun?.step));
        return (
          <div className="wb-notrun" data-tour="notrun">
            <h4>{navRow!.status === 'running' && <span className="wb-band-spin" aria-hidden="true" />}{box.title}</h4>
            <p>{box.text}</p>
            {box.note && <p className="wb-notrun-sub">{box.note}</p>}
            {navRow!.status === 'running' && <ChainLoop rows={boxRowsOf(workbench.chainProgress, stage)} variant="box" />}
            {box.own && (
              <details className="wb-own">
                <summary>{box.own.summary}</summary>
                {box.own.lockLine && <p className="wb-notrun-sub">{box.own.lockLine}</p>}
                {box.own.showBody && <div ref={bodyRef} className="wb-body" style={{ textAlign: 'left' }}>{primary}{children}</div>}
              </details>
            )}
          </div>
        );
      })() : (
        <div ref={bodyRef} className={`wb-body${lockBody ? ' wb-locked' : ''}`} aria-disabled={lockBody || undefined}>
          {children}
        </div>
      )}

      <Modal open={techOpen} onCancel={() => setTechOpen(false)} footer={null} width={640} title={`Chi tiết kỹ thuật của bước — ${STAGE_LABEL[stage]}`}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div>
            <Text strong>Trạng thái: </Text><Text code>{st?.state ?? '—'}</Text>
          </div>
          <div>
            <Text strong>Mã chặn / cần chú ý</Text>
            {reasons.length === 0 ? <div><Text type="secondary">Không có.</Text></div> : (
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {reasons.map(({ r, blocker }, i) => (
                  <li key={i}><Text code>{r.code}</Text> {blocker ? '(chặn)' : '(chú ý)'}{r.episode !== undefined ? ` · tập ${r.episode}` : ''}{looksTechnical(r.message, { loose: true }) ? <> · <Text type="secondary" style={{ wordBreak: 'break-all' }}>{technicalDetailText(r.message)}</Text></> : null}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <Text strong>Job của bước</Text>
            {stageJobs.length === 0 ? <div><Text type="secondary">Không có job đang chạy / lỗi gần đây.</Text></div> : (
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {stageJobs.map((j) => (
                  <li key={j.jobId}>
                    <Text code>{j.jobId}</Text> · {j.kind}{j.phase ? ` / ${j.phase}` : ''} ·{' '}
                    {j.state === 'running' ? `chạy từ ${j.startedAt ?? '?'}${formatJobStepProgress(j.progress) ? ` · ${formatJobStepProgress(j.progress)}` : ''}` : `lỗi lúc ${j.endedAt ?? '?'}${formatJobStepProgress(j.progress) ? ` · ở bước ${formatJobStepProgress(j.progress)}` : ''}: ${technicalDetailText((j as { note?: string }).note)}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {workbench && (
            <div>
              <Text strong>Cờ khung xương</Text>
              <pre style={{ margin: '4px 0 0', fontSize: 12 }}>{JSON.stringify(workbench.outlineFlags, null, 2)}</pre>
            </div>
          )}
          <div>
            <Text strong>Cổng AI</Text>
            {gate?.gate ? (
              <div>{gate.label}: <Text code>{gate.gate.status ?? '?'}</Text> · model <Text code>{gate.gate.model ?? '?'}</Text></div>
            ) : (
              <div><Text type="secondary">Bước này chưa ghi model.</Text></div>
            )}
          </div>
          {techExtra}
        </Space>
      </Modal>
    </section>
  );
};

export default StageFrame;
